import StatefulSettings from "../controllers/StatefulSettings";
import {
  IEventItem,
  EventItem,
  EventBasketItem,
  IEventTimerInfo,
  IEventCustomDecisionResponse,
  PermissionItem,
  IPermissionItem,
  IPermissionTimerInfo,
  PermissionBasketItem,
  TaskItem,
  ITaskItem,
  AuditMessage,
  IShowNotificationState,
  ItemAdditionOptions,
  ICustomScheduleEvent,
  TaskBasketItem,
  PenaltyBasketItem,
} from "../ts/Definitions";
import {
  AuditActionTypes,
  CountType,
  ItemTypes,
  AvailabilityType,
  ControlType,
  TaskType,
  TraceLevel,
  ProofStatus,
  ReportStyle,
  WeekendHandling,
} from "../ts/enums";
import { NotificationType } from "../controllers/messaging";
import {
  getRandomItem,
  getScheduleReason,
  getDecimalRandom,
  flipCoin,
  getRandomCountForItem,
  getRandom,
  isTimedItem,
} from "../utilities/utils";
import { convertToEventDayFromDate, scheduleEvent, schedulePermission } from "../utilities/dateUtils";
import { FEATURE_FLAG_TRACE_LOGS } from "../utilities/ConfigCache";
import uniqueId from 'lodash/uniqueId';
import { differenceInMinutes } from "date-fns";

/**
 * EventProcessor - Handles all event and permission-related operations including:
 * - Event completion and scheduling
 * - Permission requests and approvals
 * - Event/permission decision logic
 * - Adding event/permission tasks to basket
 * - Audit logging for events and permissions
 */
export class EventProcessor {
  private settings!: StatefulSettings;
  private showMessage!: (args: IShowNotificationState) => void;
  private triggerItemsAwaitingEvent!: (
    eventKey: string,
    notifyUI: boolean
  ) => void;
  private handleAutoProofRequest!: (items: (TaskBasketItem | PenaltyBasketItem)[] | undefined) => void;
  /**
   * Initialize the EventProcessor with required dependencies
   */
  public initialize(
    settings: StatefulSettings,
    showMessage: (args: IShowNotificationState) => void,
    triggerItemsAwaitingEvent: (
      eventKey: string,
      notifyUI: boolean
    ) => void,
    handleAutoProofRequest: (items: (TaskBasketItem | PenaltyBasketItem)[] | undefined) => void
  ): void {
    this.settings = settings;
    this.showMessage = showMessage;
    this.triggerItemsAwaitingEvent = triggerItemsAwaitingEvent;
    this.handleAutoProofRequest = handleAutoProofRequest;
  }

  /**
   * Adds a permission to the schedule
   */
  public addPermissionToSchedule = (
    item: PermissionItem,
    scheduleReason: string
  ): void => {
    const displayName = item.name;
    if (item.requiredCredit) {
      const cost = item.requiredCredit;
      this.settings.decrementBank(cost, displayName);
      this.auditDecrement(
        "Required Credit Permission Requested",
        displayName,
        cost
      );
    }

    const result = schedulePermission(
      this.settings,
      this.showMessage,
      item,
      false,
      new Date(),
      15,
      45,
      scheduleReason
    );

    if (!result) {
      this.showMessage({
        message: "Request denied. Outside of permitted times.",
        error: true,
        notificationType: NotificationType.ErrorInfo,
      });
      const timerInfo: IPermissionTimerInfo = {
        ...item,
        decision: false,
        exclude: true,
        isTimed: false,
        isPaused: false,
        reason: "Request denied. Outside of permitted times",
        scheduleReason,
        expired: false,
        isComplete: true,
        completedTime: new Date(),
        selectedControlType: ControlType.none,
      };
      const permissionItem = new PermissionBasketItem(timerInfo);
      permissionItem.hidden = true;
      this.settings.updatePermissionState(permissionItem);
    }
    this.auditPermission(item, result);
  }

  /**
   * Adds an event to the schedule
   */
  public addEventToSchedule = (item: EventItem, scheduleReason: string): void => {
    const result = scheduleEvent(
      this.settings,
      item,
      scheduleReason,
      new Date()
    );
    this.auditEvent(item, result !== undefined);
  }

  /**
   * Completes an event and creates basket item
   */
  public onEventComplete = (
    item: IEventItem,
    overrideDecision?: boolean,
    reason?: string,
    ignoreMessage?: boolean,
    bypassAdditionChecks?: boolean,
  ): EventBasketItem | undefined => {
    if (!item) {
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Event,
          "Unknown",
          "Event item not provided to onEventComplete",
          TraceLevel.error
        )
      );
      return;
    }

    const permissionRequestChance = item.approvalChance;
    let decision = overrideDecision;

    if (decision === undefined) {
      decision = item.availability === AvailabilityType.random
        || this.settings.hasCustomScheduleToday();
      if (!decision)
        decision =
          overrideDecision !== undefined
            ? overrideDecision
            : getDecimalRandom(0, 1) > 1 - permissionRequestChance;
    }

    if (
      decision &&
      item.requiredTasksInProgress &&
      item.requiredTasksInProgress.length > 0
    ) {
      const categoriesFound = this.settings.hasCategoriesInBasket(
        true,
        item.requiredTasksInProgress
      );
      decision =
        categoriesFound !== undefined &&
        categoriesFound.length >= item.requiredTasksInProgress.length;
      if (!decision) {
        reason = "Unable to find categories in progress for Task to fire";
        this.settings.auditUser(
          new AuditMessage(
            AuditActionTypes.Session,
            "Required Tasks Missing",
            `${item.name} requires tasks of categories ${item.requiredTasksInProgress?.join()} to be in progress but only ${categoriesFound?.join()} found in pending or in progress`,
            TraceLevel.warning
          )
        );
      }
    }

    let selectedCondition;
    if (item.options) {
      const addConditions =
        getDecimalRandom(0, 1) >
        1 - (item.optionAppliedChance ? item.optionAppliedChance : 0.5);
      selectedCondition =
        addConditions && item.options ? getRandomItem(item.options) : undefined;
    }

    const eventBasketItem = this.createEventForSchedule(
      item,
      decision,
      reason,
      selectedCondition
    );

    const enableDetailLogging = this.settings
      .getConfig()
      ?.allowedFeature(
        FEATURE_FLAG_TRACE_LOGS,
        this.settings.getControl().username,
        false
      );

    if (enableDetailLogging) {
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Session,
          "onEventComplete",
          `event ${item.name} created for schedule`,
          TraceLevel.info
        )
      );
    }

    if (!eventBasketItem) return;

    this.handleReportRequestIfNeeded(item);

    try {
      this.auditEventComplete(eventBasketItem);

      if (enableDetailLogging) {
        this.settings.auditUser(
          new AuditMessage(
            AuditActionTypes.Session,
            "onEventComplete",
            `event ${item.name} audited`,
            TraceLevel.info
          )
        );
      }

      this.addEventTasksToBasket(
        eventBasketItem,
        undefined,
        bypassAdditionChecks
      );

      if (enableDetailLogging) {
        this.settings.auditUser(
          new AuditMessage(
            AuditActionTypes.Session,
            "onEventComplete",
            `event ${item.name} tasks added to basket`,
            TraceLevel.info
          )
        );
      }

      this.handleRequiredProofs(item);

      if (!ignoreMessage && !eventBasketItem.hidden) {
        this.showMessage({
          message: eventBasketItem.getBasketText(),
          success: decision,
          error: !decision,
          notificationType: NotificationType.ScheduleInfo,
        });
      }
      if (enableDetailLogging)
        this.settings.auditUser(
          new AuditMessage(
            AuditActionTypes.Session,
            "onEventComplete",
            "triggering items as completed",
            TraceLevel.info
          )
        );
      this.triggerItemsAwaitingEvent(eventBasketItem.key, true);

      if (!ignoreMessage)
        this.showMessage({
          message: "You have a new Event task",
          notificationType: NotificationType.ScheduleInfo,
        });

      // Handle auto proof request for child tasks
      const schedule = this.settings.getTaskSchedule();
      const basketItems = Object.values(schedule).filter(t => t.key === item.key || t.key.startsWith(item.key));
      this.handleAutoProofRequest(basketItems as any[]);

      if (enableDetailLogging)
        this.settings.auditUser(
          new AuditMessage(
            AuditActionTypes.Session,
            "onEventComplete",
            "complete",
            TraceLevel.info
          )
        );

      const customEvent = (item as ICustomScheduleEvent);
      if (customEvent.gameKeys) {
        this.settings.auditUser(
          new AuditMessage(
            AuditActionTypes.Event,
            "onEventComplete",
            "Games found to schedule",
            TraceLevel.info
          )
        );
        const gamekeys = customEvent.gameKeys;
        const count = customEvent.selectedGameCountPerError || 1;
        const penalties = customEvent.gameResultPenalties || [];
        if (gamekeys && gamekeys.length > 0) {
          gamekeys.forEach((g) => {
            this.settings.addScheduledGame({ gameKey: g, count: count, penaltyKeys: penalties });
          });
        }
      }
    } catch (error) {
      console.error("Error in onEventComplete:", error);
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Session,
          "onEventComplete",
          `Error processing event ${item.name}: ${error instanceof Error ? error.message : String(error)}`,
          TraceLevel.error
        )
      );
    }

    return eventBasketItem;
  }

  private handleReportRequestIfNeeded(item: IEventItem): void {
    const itemName = (item.name ?? '').toLowerCase();
    const itemKey = (item.key ?? '').toUpperCase();
    const isDailyReport = itemKey === 'DAILY_REPORT_ID' || itemName === 'daily report';
    const isMorningReport = itemKey === 'MORNING_REPORT_ID' || itemName === 'morning report';
    if (!isDailyReport && !isMorningReport) return;

    const generalSettings = this.settings.getGeneralSettings();
    const dateDay = convertToEventDayFromDate(new Date().getDay());
    const customSchedule = this.settings.getScheduledCustomSchedule(dateDay, new Date());
    const hasCustomReportOverride = Boolean(
      customSchedule && (
        customSchedule.dailyReporting !== undefined
        || customSchedule.minReportTime !== undefined
        || customSchedule.maxReportTime !== undefined
        || customSchedule.reportStyle !== undefined
        || customSchedule.reportWordCount !== undefined
      )
    );
    const reportConfig = hasCustomReportOverride
      ? {
        dailyReporting: customSchedule?.dailyReporting ?? true,
        reportStyle: customSchedule?.reportStyle ?? generalSettings.reportStyle,
        reportWordCount: customSchedule?.reportWordCount ?? generalSettings.reportWordCount,
      }
      : {
        dailyReporting: generalSettings.dailyReporting,
        reportStyle: generalSettings.reportStyle,
        reportWordCount: generalSettings.reportWordCount,
      };

    if (!reportConfig.dailyReporting) return;
    if (isMorningReport && reportConfig.reportStyle === ReportStyle.once) return;

    let wordCount = reportConfig.reportWordCount;
    const isWeekendDay = this.settings.getIsWeekend();
    if (isWeekendDay && generalSettings.weekendHandling === WeekendHandling.harsh) {
      wordCount = wordCount * 1.5;
    } else if (isWeekendDay && generalSettings.weekendHandling === WeekendHandling.easy) {
      wordCount = wordCount * 0.5;
    }
    const count = getRandom(wordCount * 0.75, wordCount * 1.25);
    if (isMorningReport) {
      this.settings.updateMorningReportDialog({ count, open: true });
    } else {
      this.settings.updateReportDialog({ count, open: true });
    }
  }

  /**
   * Handles permission request
   */
  public onPermissionRequested = (
    itemKey: string,
    overrideDecision?: boolean,
    additionalInfo?: IEventCustomDecisionResponse
  ): PermissionBasketItem | undefined => {
    const item = this.settings.getPermission(itemKey);
    if (!item) {
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Permission,
          itemKey,
          "Permission item not found",
          TraceLevel.warning
        )
      );
      return;
    }

    const permissionSettings = this.settings.getPermissionSettings();
    const permissionRequestChance = item.approvalChance;
    let decision =
      overrideDecision !== undefined
        ? overrideDecision
        : additionalInfo
          ? additionalInfo.decision
          : getDecimalRandom(0, 1) > 1 - permissionRequestChance;
    let reason = additionalInfo ? additionalInfo.reason : "";

    if (
      decision &&
      item.requiredTasksInProgress &&
      item.requiredTasksInProgress.length > 0
    ) {
      const categoriesFound = this.settings.hasCategoriesInBasket(
        true,
        item.requiredTasksInProgress
      );
      decision =
        categoriesFound?.length !== item.requiredTasksInProgress.length;
      if (!decision) {
        reason = `${item.name} requires tasks of a category to be in progress but ${categoriesFound?.join()} not found in pending or in progress`;
        this.settings.auditUser(
          new AuditMessage(
            AuditActionTypes.Session,
            "Required Tasks Missing",
            reason,
            TraceLevel.warning
          )
        );
      }
    }

    const addCountLimits = additionalInfo
      ? additionalInfo.addCountLimits ||
      additionalInfo.expectedCount !== undefined
      : item.alwaysApplyLimit || flipCoin();
    const expectedCount = additionalInfo
      ? additionalInfo.expectedCount
      : getRandomCountForItem(
        item.count,
        permissionSettings.permissionDurationVariance,
        !isTimedItem(item.countType)
      );
    const countType = additionalInfo
      ? additionalInfo.countType
      : item.countType;
    const controlType = additionalInfo
      ? additionalInfo.selectedControlType
      : item.controlTypes && getRandomItem(item.controlTypes);
    let selectedCondition;
    if (additionalInfo) selectedCondition = additionalInfo.selectedCondition;
    else {
      const addConditions =
        getDecimalRandom(0, 1) >
        1 - (item.optionAppliedChance ? item.optionAppliedChance : 0.5);
      selectedCondition =
        addConditions && item.options ? getRandomItem(item.options) : undefined;
    }

    const permission = this.createPermissionForSchedule({
      itemKey,
      decision,
      addCountLimits,
      expectedCount,
      countType: countType,
      selectedControlType: controlType || ControlType.none,
      selectedCondition,
      reason,
    });

    if (!permission) return;

    this.auditPermissionComplete(permission);
    this.settings.updatePermissionState(permission);
    this.addPermissionTasksToBasket(permission);
    this.handleRequiredProofs(item);
    return permission;
  }

  /**
   * Adds permission to basket (used for scheduled permissions)
   */
  public addPermissionToBasket = (item: string): PermissionBasketItem | undefined => {
    if (!item) {
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Permission,
          "Unknown",
          "Permission key not provided",
          TraceLevel.warning
        )
      );
      return;
    }
    return this.onPermissionRequested(item);
  }

  /**
   * Creates an event basket item
   */
  private createEventForSchedule(
    item: IEventItem,
    decision: boolean,
    reason: string | undefined,
    selectedCondition: string | undefined
  ): EventBasketItem | undefined {
    if (!item) return;

    const timerInfo: IEventTimerInfo = {
      ...item,
      reason,
      decision,
      exclude: false,
      isPaused: false,
      isComplete: true,
      completedTime: new Date(),
      scheduleReason: getScheduleReason(item),
    };
    const event = new EventBasketItem(timerInfo);
    if (selectedCondition) {
      event.selectedCondition = selectedCondition;
    }
    event.hidden = true;
    return event;
  }

  /**
   * Creates a permission basket item
   */
  private createPermissionForSchedule({
    itemKey,
    decision,
    addCountLimits,
    expectedCount,
    countType,
    selectedControlType,
    selectedCondition,
    reason,
  }: IEventCustomDecisionResponse): PermissionBasketItem | undefined {
    const item = this.settings.getPermission(itemKey);
    if (!item) return;

    const timerInfo: IPermissionTimerInfo = {
      ...item,
      reason,
      decision,
      expired: false,
      exclude: false,
      isPaused: false,
      isTimed: false,
      isComplete: true,
      completedTime: new Date(),
      selectedControlType: ControlType.none,
      scheduleReason: getScheduleReason(item),
    };
    const permission = new PermissionBasketItem(timerInfo);
    permission.hidden = true;
    const now = new Date();

    if (decision) {
      if (addCountLimits && expectedCount && countType && isTimedItem(countType)) {
        permission.expectedCount = expectedCount;
        permission.countType = countType;
        permission.isTimed = true;
      }
      if (selectedControlType) {
        permission.selectedControlType = selectedControlType;
      }
      if (selectedCondition) {
        permission.selectedCondition = selectedCondition;
      }
    }
    return permission;
  }

  /**
   * Adds event tasks to the basket
   */
  private addEventTasksToBasket(
    basketItem: EventBasketItem,
    additionalInfo: ItemAdditionOptions | undefined,
    bypassAdditionChecks?: boolean
  ): any[] | undefined {
    if (!basketItem) {
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Event,
          "Unknown",
          "Event basket item not provided",
          TraceLevel.warning
        )
      );
      return;
    }

    const customEvent = basketItem as unknown as ICustomScheduleEvent;
    const shouldAwaitTaskComplete = customEvent.awaitTaskComplete === true;
    let monitoredDurationMinutes: number | undefined;
    if (shouldAwaitTaskComplete) {
      if (customEvent.runTasksToNextEvent) {
        const nextScheduledEvent = this.settings
          .getScheduleInfo()
          .filter((scheduleItem) =>
            scheduleItem.itemType === ItemTypes.event
            && scheduleItem.key !== basketItem.key
            && !!scheduleItem.startTime
            && new Date(scheduleItem.startTime).getTime() > Date.now()
          )
          .sort((left, right) => {
            const leftTime = new Date(left.startTime as Date).getTime();
            const rightTime = new Date(right.startTime as Date).getTime();
            return leftTime - rightTime;
          })[0];
        if (nextScheduledEvent?.startTime) {
          monitoredDurationMinutes = Math.max(1, differenceInMinutes(new Date(nextScheduledEvent.startTime), new Date()));
        }
      }
      if (!monitoredDurationMinutes && (customEvent.taskDurationMinutes ?? 0) > 0) {
        monitoredDurationMinutes = customEvent.taskDurationMinutes;
      }
      if (!monitoredDurationMinutes) {
        monitoredDurationMinutes = 1;
      }
    }

    const eventTaskOptions: ItemAdditionOptions = {
      ...additionalInfo,
      customScheduleKey: basketItem.key,
      decision: basketItem.decision,
    };

    return this.settings.addTaskToSchedule(
      new TaskItem({
        ...basketItem,
        itemOptions: {},
        countType: shouldAwaitTaskComplete ? CountType.minutes : 0,
        credit: 0,
        description: basketItem.description,
        img: basketItem.img,
        imgId: basketItem.imgId,
        availabilityDayType: basketItem.availabilityDayType,
        itemType: ItemTypes.event,
        key: basketItem.key,
        preSelect: 0,
        count: shouldAwaitTaskComplete ? monitoredDurationMinutes : 0,
        monitoredTimer: shouldAwaitTaskComplete,
        name: basketItem.getBasketText(),
        creditTaskType: TaskType.acknowledgement,
      } as ITaskItem),
      `The ${basketItem.name} event specifies this task to be selected whenever it runs.`,
      basketItem as unknown as EventItem,
      eventTaskOptions,
      bypassAdditionChecks
    );
  }

  /**
   * Adds permission tasks to the basket
   */
  private addPermissionTasksToBasket(basketItem: PermissionBasketItem): void {
    const item = this.settings.getPermission(basketItem.key);
    if (!item) return;

    let { countType } = item;
    const { expectedCount } = basketItem;

    if (!basketItem.decision) {
      countType = 0;
    } else if (isTimedItem(countType) && !expectedCount) {
      countType = 0;
    }

    this.settings.addTaskToSchedule(
      new TaskItem({
        countType,
        credit: 0,
        description: item.description,
        img: item.img,
        imgId: item.imgId,
        availabilityDayType: item.availabilityDayType,
        itemType: ItemTypes.permission,
        key: `${item.key},${uniqueId()}`,
        preSelect: 0,
        count: expectedCount,
        name: basketItem.getBasketText(),
        creditTaskType: TaskType.acknowledgement,
      } as ITaskItem),
      `The ${item.name} permission specifies this task to be selected whenever it runs.`,
      item,
      {
        expectedCount,
        countType,
        selectedCondition: basketItem.selectedCondition,
        decision: basketItem.decision,
      }
    );
  }

  /**
   * Handles required proofs for events/permissions
   */
  private handleRequiredProofs(item: IPermissionItem | IEventItem): void {
    if (!item || !item.proofTaskKeys || item.proofTaskKeys.length === 0) return;

    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Event,
        item.name,
        `${item.proofTaskKeys.length} Proofs Required`,
        TraceLevel.info
      )
    );

    item.proofTaskKeys.forEach((key) => {
      const proofTask = this.settings.getTask(key);
      if (!proofTask) {
        this.settings.auditUser(new AuditMessage(AuditActionTypes.Event, item.name, `Proof task with key ${key} not found for required proof.`, TraceLevel.warning));
        return;
      }
      // lets just request proof on everything we have been asked for proof on regardless of if it is in the schedule or not, as it may be added later and we want to make sure we have the proof task ready to go when it is
      this.settings.addTaskToSchedule(
        new TaskItem({
          itemType: ItemTypes.task,
          key: `${key}-${proofTask.key}`,
          name: `Proof for ${proofTask.name}`,
          description: "Proof has been requested for this task. Please provide proof to complete.",
          img: proofTask.img,
          imgId: proofTask.imgId,
          credit: 0,
          count: 1,
          countType: 0,
          preSelect: 0,
          creditTaskType: TaskType.acknowledgement,
          proofStatus: ProofStatus.alwaysRequestProof, // setting this to always request proof so that if the task is added to the schedule later we will still request proof for it
          rollOverEventDays: true,
        } as ITaskItem),
        "Proof requested was not possible at time triggered.",
        item,
        undefined,
        true
      );

    });
  }

  // Audit helper methods
  private auditEvent(item: EventItem, available: boolean): void {
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Event,
        item.name,
        "Requested",
        TraceLevel.info,
        available
      )
    );
  }

  private auditPermission(item: PermissionItem, available: boolean): void {
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Permission,
        item.name,
        "Requested",
        TraceLevel.info,
        available
      )
    );
  }

  private auditEventComplete(item: EventBasketItem): void {
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Event,
        item.name,
        `Decision: ${item.getBasketText()}`,
        TraceLevel.info,
        item.decision
      )
    );
  }

  private auditPermissionComplete(item: PermissionBasketItem): void {
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Permission,
        item.name,
        `Decision: ${item.getBasketText()}`,
        TraceLevel.info,
        item.decision
      )
    );
  }

  private auditDecrement(name: string, action: string, cost: number): void {
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Task,
        name,
        action,
        TraceLevel.info,
        undefined,
        Math.abs(cost) * -1
      )
    );
  }
}
